import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, mergeMap, switchMap, take } from 'rxjs/operators';
import { TokenService } from '../service/token.service';
import { Router } from '@angular/router';

@Injectable()
export class BearerInterceptor implements HttpInterceptor {
  /** Empêche N requêtes en échec de déclencher N déconnexions + N navigations. */
  private redirectingToLogin = false;

  constructor(private tokenService: TokenService, private router: Router) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.includes('plugins.stripo.email')) {
      return next.handle(req);
    }

    let token = this.tokenService.currentToken?.getPayload()?.access_token;

    // Always enable credentials for backend requests to allow cookies (Trusted Device)
    req = req.clone({
      withCredentials: true
    });

    if (token && !this.isRefreshOrLoginRequest(req)) {
      return this.tokenService.verify().pipe(
        // Filet de sécurité : quoi qu'il arrive, on ne traite qu'une seule
        // réponse de verify(). Une source qui ré-émet relancerait la requête
        // HTTP à chaque émission.
        take(1),
        mergeMap((isValid: boolean) => {
          if (isValid) {
            // Le refresh a pu produire un nouveau token : on relit la valeur
            // courante plutôt que celle capturée avant verify().
            const freshToken = this.tokenService.currentToken?.getPayload()?.access_token || token;
            return next.handle(this.addToken(req, freshToken));
          }

          // verify() a déjà tenté le refresh : ici la session est bien perdue.
          this.forceLogout();
          return throwError(() => new Error('Session expired'));
        }),
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return this.handle401Error(req, next);
          }
          return throwError(() => error);
        })
      );
    } else {
      return next.handle(req);
    }
  }

  private isRefreshOrLoginRequest(req: HttpRequest<any>): boolean {
    return req.url.endsWith('/api/token/refresh/') || req.url.endsWith('/api/token/') || req.url.endsWith('/api/token/verify/');
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      headers: request.headers.set('Authorization', 'Bearer ' + token)
    });
  }

  /**
   * Déconnexion idempotente. Sans ce garde-fou, chaque requête en vol appelait
   * `clearToken()` — qui déclenche un `tokenChange` — puis `navigate()`, ce qui
   * relançait les autres requêtes en attente : boucle qui saturait l'onglet et
   * inondait l'API.
   */
  private forceLogout(): void {
    if (this.redirectingToLogin) return;
    this.redirectingToLogin = true;

    this.tokenService.clearToken();
    this.router.navigate(['/auth/login']).finally(() => {
      this.redirectingToLogin = false;
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    // TokenService.refresh() dédoublonne déjà les appels concurrents et émet
    // toujours exactement une valeur avant de compléter.
    return this.tokenService.refresh().pipe(
      take(1),
      switchMap((isRefreshed: boolean) => {
        if (isRefreshed) {
          const newToken = this.tokenService.currentToken?.getPayload()?.access_token;
          if (newToken) {
            return next.handle(this.addToken(request, newToken));
          }
        }

        this.forceLogout();
        return throwError(() => new Error('Session expired'));
      }),
      catchError((err) => {
        // `refresh()` n'émet jamais d'erreur : on ne voit ici que celles de la
        // requête rejouée. Seul un 401 signifie une session perdue — un 500 ou
        // un 404 ne doit pas déconnecter l'utilisateur.
        if (err instanceof HttpErrorResponse && err.status === 401) {
          this.forceLogout();
        }
        return throwError(() => err);
      })
    );
  }
}
